import { AfterContentChecked, Component, Input, OnInit } from '@angular/core';
import {
  CdkDragDrop,
  copyArrayItem,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Config, ConstraintPositionConfig } from '../models/ui-config';
import { find, forEach, keys, map } from 'lodash';
import { CommonUtilsService } from '../shared/common-utils.service';
import { JsonDynamicComponentService } from '../json-dynamic-component.service';
import { FormControlValueChangedAction } from '../models/form-value-changed-event';
import { ConfirmDialogComponent } from '../shared/components/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogData } from '../models/confirm-dialog-data';
import { DEFAULT_DIALOG_CONFIG } from '../constants/lib-constants';

interface DragDropItem {
  value: string;
  disabled: boolean;
  deleted: boolean;
}

@Component({
  selector: 'ra-dynamic-list-drag-drop',
  templateUrl: './dynamic-list-drag-drop.component.html',
  styleUrls: ['./dynamic-list-drag-drop.component.scss'],
  standalone: false,
})
export class DynamicListDragDropComponent
  implements OnInit, AfterContentChecked
{
  /** Persisted with model config via hidden checkbox in ui_control_settings. */
  private readonly constraintsResetFlagKey =
    'CONSTRAINTS_RESET_TO_DEFAULTS_SELECTED';

  @Input() readOnly: boolean = false;
  @Input() parentFormGroup: FormGroup;
  @Input() form: FormGroup;
  @Input() currentConfig: Config;
  keys = keys;

  allValuesMap: DragDropItem[];
  sourceList: string[];
  resultantList: string[];

  get resetFlagControl(): FormControl<boolean> | null {
    const c = this.parentFormGroup?.get(this.constraintsResetFlagKey);
    return c instanceof FormControl ? (c as FormControl<boolean>) : null;
  }

  /** Mirrors persisted PRICE_PLUS.CONSTRAINTS_RESET_TO_DEFAULTS_SELECTED. */
  get resetToDefaultsChecked(): boolean {
    return !!this.resetFlagControl?.value;
  }

  get isDisabled(): boolean {
    return (
      this.readOnly ||
      !!this.parentFormGroup?.get(this.currentConfig?.property)?.disabled
    );
  }

  /** Disabled when read-only or the constraints control is disabled; reset-to-defaults does not lock lists. */
  get isDragDropInteractionDisabled(): boolean {
    return this.isDisabled;
  }

  constructor(
    private commonUtils: CommonUtilsService,
    private jsonDynamicComponentService: JsonDynamicComponentService,
    private dialog: MatDialog,
  ) {}

  get isConstraintsApplyScreen(): boolean {
    return (
      this.currentConfig?.property === 'CONSTRAINTS_FUNCTION_LIST' &&
      Array.isArray(this.currentConfig?.defaultValue) &&
      (this.currentConfig.defaultValue as string[]).length > 0
    );
  }

  /** Confirm → reset list (triggered when user checks Reset to defaults). */
  private openConfirmResetDefaultsDialog(): void {
    if (!this.isConstraintsApplyScreen) {
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        ...DEFAULT_DIALOG_CONFIG,
        data: {
          title: 'Reset constraints to defaults?',
          message:
            'This replaces your current constraint list with the default list. Any custom order or selection will be lost.',
          footnote: '',
          showCancelBtn: true,
          okButtonLabel: 'Reset to defaults',
          cancelButtonLabel: 'Cancel',
        } as ConfirmDialogData,
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.applyResetToDefaults();
          this.resetFlagControl?.setValue(true, { emitEvent: false });
          setTimeout(() => {
            this.jsonDynamicComponentService.emitFormValueChanged({
              formGroup: this.parentFormGroup,
              property: this.currentConfig.property,
              action: FormControlValueChangedAction.ConstraintsResetToDefaults,
              config: this.currentConfig,
            });
          }, 0);
        } else {
          this.resetFlagControl?.setValue(false, { emitEvent: false });
        }
      });
  }

  private applyResetToDefaults(): void {
    this.resultantList = [
      ...((this.currentConfig.defaultValue as string[]) ?? []),
    ];
    this.allValuesMap =
      map(this.currentConfig?.values, (value) => ({
        value,
        disabled: false,
        deleted: !this.resultantList.includes(value),
      })) || [];
    this.sourceList = map(this.allValuesMap, 'value') || [];
    this.updateOriginalList();
    this.updateResultantListWithConstraints();
    this.filterDisabledValues();
    const control = this.parentFormGroup?.get(this.currentConfig.property);
    control?.setValue([...this.resultantList], { emitEvent: false });
    control?.updateValueAndValidity({ emitEvent: false });
    this.onValueChanged();
  }

  onResetToDefaultsCheckboxChange(checked: boolean): void {
    const c = this.resetFlagControl;
    if (!c || this.isDisabled) {
      return;
    }
    c.markAsDirty();
    c.markAsTouched();
    if (!checked) {
      c.setValue(false, { emitEvent: false });
      return;
    }
    c.setValue(true, { emitEvent: false });
    this.openConfirmResetDefaultsDialog();
  }

  ngOnInit(): void {
    this.ensureConstraintsResetFlagControl();
    this.allValuesMap =
      map(this.currentConfig?.values, (value) => ({
        value,
        disabled: false,
        deleted: !this.parentFormGroup
          .get(this.currentConfig.property)
          .value?.includes(value),
      })) || [];
    this.sourceList = map(this.allValuesMap, 'value') || [];
    this.resultantList =
      this.parentFormGroup?.get(this.currentConfig.property)?.value || [];
    this.updateOriginalList();
  }

  /** User edited the constraint list after a reset — clear persisted reset flag so “Reset to defaults” can be chosen again. */
  private clearResetFlagAfterManualListEdit(): void {
    if (
      !this.isConstraintsApplyScreen ||
      !this.resetToDefaultsChecked ||
      this.isDisabled
    ) {
      return;
    }
    this.resetFlagControl?.setValue(false, { emitEvent: false });
  }

  /** Older configs without ui flag: add control so the checkbox still works. */
  private ensureConstraintsResetFlagControl(): void {
    if (!this.isConstraintsApplyScreen || !this.parentFormGroup) {
      return;
    }
    if (!this.parentFormGroup.get(this.constraintsResetFlagKey)) {
      this.parentFormGroup.addControl(
        this.constraintsResetFlagKey,
        new FormControl(false, { nonNullable: true }),
      );
    }
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        this.resultantList,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      copyArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      const item = (
        event.item.element.nativeElement as HTMLElement
      ).textContent?.trim();
      this.allValuesMap = map(this.allValuesMap, (object) =>
        object.value !== item ? object : { ...object, deleted: false },
      );
      this.parentFormGroup
        ?.get(this.currentConfig.property)
        ?.setErrors({ required: null });
      this.parentFormGroup
        ?.get(this.currentConfig.property)
        ?.updateValueAndValidity();
    }
    this.clearResetFlagAfterManualListEdit();
    this.onValueChanged();
  }

  remove(i: number) {
    const item = this.resultantList.splice(i, 1)[0];
    const obj = find(this.allValuesMap, { value: item });
    obj.deleted = true;
    if (this.resultantList.length === 0) {
      this.parentFormGroup
        ?.get(this.currentConfig.property)
        ?.setErrors({ required: true });
    }
    this.clearResetFlagAfterManualListEdit();
    this.onValueChanged();
  }

  private onValueChanged() {
    if (this.currentConfig.property !== 'CONSTRAINTS_FUNCTION_LIST') {
      return;
    }
    setTimeout(() => {
      this.jsonDynamicComponentService.emitFormValueChanged({
        formGroup: this.parentFormGroup,
        property: this.currentConfig.property,
        action: FormControlValueChangedAction.ValueChanged,
        config: this.currentConfig,
      });
    }, 0);
  }

  updateOriginalList() {
    // Condition expressions reference GLOBAL model-level flags (PRICE_PLUS.*).
    // The flat list is bound to the root form, so this.form.root === this.form.
    // The per-group instance is bound to the group's nested sub-form, so we
    // climb to the root FormGroup (which holds PRICE_PLUS) to resolve them.
    const interpolationBase = (this.form?.root ?? this.form)?.value;
    forEach(
      this.currentConfig?.dependentConstraintsConditionExpression,
      (val, key) => {
        const obj = find(this.allValuesMap, { value: key });
        const prev_disabled = obj.disabled;
        obj.disabled = !this.commonUtils.safeEvaluate(
          this.commonUtils.getInterpolatedValue(val, interpolationBase),
        );
        obj.deleted = prev_disabled && !obj.disabled ? false : true;
      },
    );
  }

  filterDisabledValues() {
    this.resultantList = this.resultantList.filter((value) => {
      const obj = find(this.allValuesMap, { value });
      return !obj.disabled;
    });
  }

  updateResultantListWithConstraints() {
    forEach(this.currentConfig?.constraintsToAddIfEnabled, (constraintObj) => {
      const constraintName: string = keys(constraintObj)[0];
      const positionConfig: ConstraintPositionConfig =
        constraintObj[constraintName];
      const obj = find(this.allValuesMap, { value: constraintName });
      if (obj.disabled || obj.deleted) {
        return;
      }

      let index = this.resultantList.length;
      if (
        positionConfig.positionType == 'fixed' &&
        positionConfig.index !== undefined &&
        positionConfig.index !== null
      ) {
        index = Number(positionConfig.index);
        index = Math.min(index, this.resultantList.length + 1);
        index = Math.max(index, 0);
      } else if (positionConfig.positionType == 'relative') {
        const relativeTo = positionConfig?.relativeTo || '';
        index = this.resultantList.indexOf(relativeTo);
        if (index === -1) {
          index = this.resultantList.length;
        } else if (positionConfig.position === 'after') {
          index = index + 1;
        }
      }
      if (!this.resultantList.includes(constraintName)) {
        this.resultantList.splice(index, 0, constraintName);
      }
    });
  }

  ngAfterContentChecked(): void {
    if (this.allValuesMap) {
      this.updateOriginalList();
      this.updateResultantListWithConstraints();
      this.filterDisabledValues();
      this.parentFormGroup
        ?.get(this.currentConfig.property)
        ?.setValue(this.resultantList, { emitEvent: false });
    }
  }
}
